import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  SchoolMember,
  SchoolMemberData,
  SchoolMemberExistingData,
  SchoolMemberUpdateData,
  SchoolMessageData,
  SchoolMessageResult,
} from '../../models/school-user.model';
import { environment } from '../../../../environments/environment';
import { reloadOnAccountCreated } from '../../../users/services/reload-on-account-created';
import { Observable, tap } from 'rxjs';
import { NotificationService } from '../../../core/services/notification-service/notification-service';
import { AuthService } from '../../../auth/services/auth.service';
import { isApiError } from '../../../core/errors/api-error';
import { ApiErrorCode } from '../../../core/errors/api-error-code';
import { ApiErrorHandler } from '../../../core/errors/api-error-handler';

@Injectable({
  providedIn: 'root',
})
export class SchoolUserService {
  private readonly http = inject(HttpClient);
  private readonly notificationService = inject(NotificationService);
  private readonly apiErrors = inject(ApiErrorHandler);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly #schoolId = signal<string | null>(null);

  #membersResource = httpResource<SchoolMember[]>(() => {
    if (!this.authService.isAuthenticated()) return undefined;
    const schoolId = this.#schoolId();
    if (!schoolId) return undefined;
    return `${environment.apiUrl}/schools/${schoolId}/users`;
  });

  readonly members = computed(() => {
    if (this.#membersResource.status() === 'error') {
      return [];
    }
    return this.#membersResource.value() ?? [];
  });

  #mutationLoading = signal(false);
  readonly isLoading = computed(() => this.#membersResource.isLoading() || this.#mutationLoading());

  /** The signed-in user's own row, or `null` for a system admin looking at a school not theirs. */
  readonly currentMember = computed(() => {
    const email = this.authService.currentUser()?.email?.toLowerCase();
    if (!email) return null;
    return this.members().find((m) => m.email.toLowerCase() === email) ?? null;
  });

  /** Everyone the signed-in user can write to. */
  readonly otherMembers = computed(() => {
    const self = this.currentMember();
    return this.members().filter((m) => m !== self);
  });

  readonly canManage = computed(
    () => this.authService.isAdmin() || this.currentMember()?.schoolRole === 'admin',
  );

  constructor() {
    effect(() => {
      if (!this.authService.isAuthenticated()) {
        this.#schoolId.set(null);
      }
    });

    effect(() => {
      if (this.#membersResource.status() === 'error') {
        const error = this.#membersResource.error();
        // Not this user's school, or no such school: nothing to show here, back to the start.
        if (
          isApiError(error, ApiErrorCode.FORBIDDEN) ||
          isApiError(error, ApiErrorCode.NOT_FOUND)
        ) {
          void this.router.navigate(['/']);
          return;
        }
        this.apiErrors.notify(error, 'SchoolUserService.loadMembers');
      }
    });
  }

  setSchoolId(id: string): void {
    this.#schoolId.set(id);
  }

  /** Refetches the member list — for a caller that changed it along a path this service cannot see. */
  reload(): void {
    this.#membersResource.reload();
  }

  /**
   * Posts the new member and hands the caller the request, so a dialog that owns the action can
   * see it fail and keep the filled-in form open. The list refresh stays here; the error does not.
   */
  createMember(schoolId: string, data: SchoolMemberData): Observable<SchoolMember> {
    return this.http
      .post<SchoolMember>(`${environment.apiUrl}/schools/${schoolId}/users`, data)
      .pipe(reloadOnAccountCreated(() => this.#membersResource.reload()));
  }

  /** @see createMember — the caller owns the failure. */
  addExistingMember(schoolId: string, data: SchoolMemberExistingData): Observable<void> {
    return this.http
      .post<void>(`${environment.apiUrl}/schools/${schoolId}/users/existing`, data)
      .pipe(
        tap(() =>
          this.notificationService.showSuccess(
            'Pozvánka byla odeslána. Uživatel se stane členem školy po jejím přijetí.',
          ),
        ),
      );
  }

  /** @see createMember — the caller owns the failure, and the success message too. */
  sendMessage(schoolId: string, data: SchoolMessageData): Observable<SchoolMessageResult> {
    return this.http.post<SchoolMessageResult>(
      `${environment.apiUrl}/schools/${schoolId}/messages`,
      data,
    );
  }

  resendInvitation(schoolId: string, userId: string) {
    this.#mutationLoading.set(true);
    this.http
      .post<void>(`${environment.apiUrl}/schools/${schoolId}/users/${userId}/resend-invitation`, {})
      .subscribe({
        next: () => {
          this.#mutationLoading.set(false);
          this.notificationService.showSuccess('Pozvánka byla znovu odeslána.');
        },
        error: (error: unknown) => {
          this.#mutationLoading.set(false);
          this.apiErrors.notify(error, 'SchoolUserService.resendInvitation');
        },
      });
  }

  /** @see createMember — the caller owns the failure. */
  updateMember(
    schoolId: string,
    userId: string,
    data: SchoolMemberUpdateData,
  ): Observable<SchoolMember> {
    return this.http
      .put<SchoolMember>(`${environment.apiUrl}/schools/${schoolId}/users/${userId}`, data)
      .pipe(tap(() => this.#membersResource.reload()));
  }

  deleteMember(schoolId: string, userId: string) {
    this.#mutationLoading.set(true);
    this.http.delete(`${environment.apiUrl}/schools/${schoolId}/users/${userId}`).subscribe({
      next: () => {
        this.#mutationLoading.set(false);
        this.#membersResource.reload();
      },
      error: (error: unknown) => {
        this.#mutationLoading.set(false);
        this.apiErrors.notify(error, 'SchoolUserService.deleteMember');
      },
    });
  }
}
